import ReactHtmlParser from 'react-html-parser';
import { useNavigate, useParams } from 'react-router';

import { Typography, Card, styled, Grid, Button, Box } from '@mui/material';

import { ConfirmDialog } from '../../../components/dialogs';
import { useDeactivateReportedPost, useGetReportedPostByPostId } from '../../../lib/post/hooks';
import { useReinstateReportedPost } from '../../../lib/post/hooks/useReinstateReportedPostById';
import { extractImagesFromPostContent } from '../../../lib/util/helpers';

function ReportedPostEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const reportedPostId = id ?? '';
  const getReportedPostById = useGetReportedPostByPostId(reportedPostId);
  const getReportedPosts = getReportedPostById.data;
  let ready = false;

  ready = !getReportedPostById.isLoading;

  const postContent = (data: any) => {
    const { images, cleanedContent } = extractImagesFromPostContent(data);

    return (
      <>
        <Box sx={{ display: 'flex', gap: '24px' }}>
          {images.map((content, index) => (
            <Box
              key={index}
              component="img"
              sx={{
                width: '250px',
                height: '250px',
                objectFit: 'cover',
              }}
              alt="Comment Image"
              src={content.src}
            />
          ))}
        </Box>
        {ReactHtmlParser(cleanedContent)}
      </>
    );
  };

  const deactivatePost = useDeactivateReportedPost();
  const reinstatePost = useReinstateReportedPost();

  const handleDeactivate = async (id: string) => {
    await deactivatePost.mutate({ reportedPostId: id });
    navigate(-1);
  };

  const handleReinstate = async (id: string) => {
    await reinstatePost.mutate(id);
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  return (
    <>
      {ready && getReportedPosts && (
        <Container>
          <TopBar>
            <Typography variant="h5">Reported Article Detail: {getReportedPosts[0].post.title} </Typography>
          </TopBar>

          <StyledCard>
            <StyledGridPostContent item xs={12}>
              <StyledTypography>Article Content: </StyledTypography>
              {postContent(getReportedPosts[0]?.post.content)}
            </StyledGridPostContent>
            <StyledMainGrid container rowSpacing={4} mb={4}>
              {getReportedPosts.map((reportedPost: any, index: number) => {
                const reporter =
                  reportedPost?.reporter.firstName && reportedPost?.reporter.lastName
                    ? `${reportedPost?.reporter.firstName} ${reportedPost?.reporter.lastName}`
                    : reportedPost?.reporter.username;
                return (
                  <StyledGrid container rowSpacing={4} mb={4} key={index}>
                    <Grid item xs={12}>
                      <Typography>Reported Date: {new Date(reportedPost?.created).toLocaleDateString()}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Reporter Username: {reporter} </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Reason: {reportedPost?.reason}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Description: {reportedPost?.description}</Typography>
                    </Grid>
                  </StyledGrid>
                );
              })}
            </StyledMainGrid>
            <ButtonContainer>
              <Buttons>
                <Button onClick={() => navigate(-1)} type="button">
                  Cancel
                </Button>
                <ConfirmDialog
                  trigger={(open) => (
                    <Button
                      onClick={open}
                      type="button"
                      variant="contained"
                      disabled={getReportedPosts[0]?.post.deactivated ? true : false}
                    >
                      {getReportedPosts[0]?.post.deactivated ? 'Deactivated' : 'Deactivate'}
                    </Button>
                  )}
                  title="Deactivate Reported Post"
                  description="Are you sure you want to deactivate this reported post?"
                  cancelBtnText={'Cancel'}
                  submitBtnText={'Submit'}
                  onConfirm={async (close) => {
                    await handleDeactivate(getReportedPosts[0]?.post.id);
                    close();
                  }}
                />
                {getReportedPosts[0]?.post.deactivated && (
                  <ConfirmDialog
                    trigger={(open) => (
                      <Button onClick={open} type="button" variant="contained">
                        Reinstate
                      </Button>
                    )}
                    title="Reinstate Deactivated Post"
                    description="Are you sure you want to reinstate this deactivated post?"
                    cancelBtnText={'Cancel'}
                    submitBtnText={'Submit'}
                    onConfirm={async (close) => {
                      await handleReinstate(getReportedPosts[0]?.post.id);
                      close();
                    }}
                  />
                )}
              </Buttons>
            </ButtonContainer>
          </StyledCard>
        </Container>
      )}
    </>
  );
}

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  padding: '40px',
});

const StyledMainGrid = styled(Grid)({
  display: 'block',
  maxHeight: '1200px',
  overflow: 'scroll',
  marginTop: '0',
});

const StyledGridPostContent = styled(Grid)({
  borderBottom: '1px solid rgba(224, 224, 224, 1)',
  paddingBottom: '32px',
  marginBottom: '32px',
});

const StyledGrid = styled(Grid)({
  borderTop: '1px solid rgba(224, 224, 224, 1)',
  paddingBottom: '32px',
  '&:first-child': {
    borderTop: 0,
  },
});

const StyledTypography = styled(Typography)({
  display: 'inline-block',
  marginRight: '3px',
  '> p': {
    margin: '0',
  },
});

const ButtonContainer = styled('div')({
  marginTop: '40px',
  alignSelf: 'flex-end',
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '7px',
  '& > *': {
    margin: '5px',
  },
});

export default ReportedPostEdit;
